// src/pages/Checkout/CheckoutPage.js
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { paymentService, orderService } from '../../services/api';
import './CheckoutPage.css';

import { useDocumentTitle } from '../../hooks/useDocumentTitle';
// Clé publique Stripe (pk_test_... — définie dans Frontend/.env)
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

// Style du champ carte pour s'accorder au thème sombre
const CARD_STYLE = {
  style: {
    base: {
      color: '#fafaf8',
      fontFamily: '"Jost", "Helvetica Neue", sans-serif',
      fontSize: '16px',
      '::placeholder': { color: '#888888' },
    },
    invalid: { color: '#e05252', iconColor: '#e05252' },
  },
};

export default function CheckoutPage() {
  useDocumentTitle('Commande');

  const { items, total, count, clearCart } = useCart();
  const { isLoggedIn, user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1=adresse, 2=recap, 3=paiement, 4=confirmation
  const [orderId, setOrderId] = useState(null);
  const [reference, setReference] = useState(null);   // référence de suivi (commande invitée)
  const [error, setError] = useState('');

  // Commande sans compte : activée par la visiteuse depuis l'écran de choix.
  const [modeInvite, setModeInvite] = useState(false);
  const [emailInvite, setEmailInvite] = useState('');

  const [adresse, setAdresse] = useState({
    prenom: user?.prenom || '',
    nom: user?.nom || '',
    rue: '',
    complement: '',
    code_postal: '',
    ville: '',
    telephone: '',
  });

  const fraisLivraison = total >= 50 ? 0 : 4.90;
  const totalFinal = total + fraisLivraison;

  // Redirect si panier vide
  if (items.length === 0 && step < 4) {
    return (
      <div className="checkout">
        <div className="container checkout__inner">
          <div className="checkout__empty">
            <h1>Votre panier est vide</h1>
            <p>Ajoutez des articles avant de passer commande.</p>
            <Link to="/catalogue" className="btn-or">Decouvrir la collection</Link>
          </div>
        </div>
      </div>
    );
  }

  // Visiteuse non connectée : on lui laisse le choix plutôt que de la bloquer.
  // Trois voies possibles — se connecter, créer un compte, ou commander en
  // invitée. Aucune n'est imposée.
  if (!isLoggedIn && !modeInvite) {
    return (
      <div className="checkout">
        <div className="container checkout__inner">
          <div className="checkout__choix">
            <h1 className="checkout__choix-titre">Comment souhaitez-vous commander ?</h1>
            <p className="checkout__choix-sous">
              Votre panier est conservé quel que soit votre choix.
            </p>

            <div className="checkout__choix-grille">
              <div className="checkout__choix-carte">
                <h2>J'ai déjà un compte</h2>
                <p>Retrouvez vos adresses enregistrées et l'historique de vos commandes.</p>
                <Link to="/connexion" state={{ from: { pathname: '/checkout' } }} className="btn-or">
                  Se connecter
                </Link>
              </div>

              <div className="checkout__choix-carte checkout__choix-carte--invite">
                <h2>Commander sans compte</h2>
                <p>
                  Renseignez simplement votre email et votre adresse de livraison.
                  Une référence de suivi vous sera communiquée.
                </p>
                <button type="button" className="btn-or" onClick={() => setModeInvite(true)}>
                  Continuer en tant qu'invitée
                </button>
              </div>

              <div className="checkout__choix-carte">
                <h2>Créer un compte</h2>
                <p>Suivez vos commandes, gagnez du temps lors de vos prochains achats.</p>
                <Link to="/inscription" state={{ from: { pathname: '/checkout' } }} className="btn-outline">
                  Créer un compte
                </Link>
              </div>
            </div>

            <p className="checkout__choix-note">
              Déjà commandé sans compte ? <Link to="/suivi">Suivre ma commande</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  function handleAdresseChange(e) {
    setAdresse(a => ({ ...a, [e.target.name]: e.target.value }));
    setError('');
  }

  function validerAdresse() {
    if (!isLoggedIn && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInvite)) {
      setError('Veuillez saisir une adresse email valide.');
      return false;
    }
    if (!adresse.prenom || !adresse.nom || !adresse.rue || !adresse.code_postal || !adresse.ville || !adresse.telephone) {
      setError('Tous les champs marques * sont obligatoires.');
      return false;
    }
    if (!/^\d{5}$/.test(adresse.code_postal)) {
      setError('Le code postal doit contenir 5 chiffres.');
      return false;
    }
    return true;
  }

  // Appelé par le formulaire Stripe une fois le paiement réussi
  // et la commande créée côté serveur.
  function handlePaymentSuccess(newOrderId, nouvelleReference) {
    setOrderId(newOrderId);
    setReference(nouvelleReference);
    clearCart();
    setStep(4);
  }

  return (
    <div className="checkout">
      <div className="container checkout__inner">

        {/* Stepper */}
        <div className="checkout__stepper">
          {['Livraison', 'Recapitulatif', 'Paiement', 'Confirmation'].map((label, i) => (
            <div key={i} className={`checkout__step ${step > i + 1 ? 'done' : ''} ${step === i + 1 ? 'active' : ''}`}>
              <div className="checkout__step-circle">{step > i + 1 ? '\u2713' : i + 1}</div>
              <span className="checkout__step-label">{label}</span>
            </div>
          ))}
        </div>

        {error && <div className="checkout__error" role="alert">{error}</div>}

        {/* === ETAPE 1: ADRESSE === */}
        {step === 1 && (
          <div className="checkout__section">
            <h2 className="checkout__section-titre">Adresse de livraison</h2>
            <form className="checkout__form" onSubmit={e => { e.preventDefault(); if (validerAdresse()) setStep(2); }}>
              {/* Commande sans compte : l'email sert à la confirmation et au
                  suivi de commande. C'est la seule donnée demandée en plus. */}
              {!isLoggedIn && (
                <div className="checkout__field">
                  <label htmlFor="email_invite">Adresse email *</label>
                  <input
                    id="email_invite" type="email" value={emailInvite}
                    onChange={e => { setEmailInvite(e.target.value); setError(''); }}
                    placeholder="votre@email.com" required
                  />
                  <p className="checkout__field-aide">
                    Elle nous sert à vous transmettre votre référence de suivi.
                    Aucun compte n'est créé.
                  </p>
                </div>
              )}
              <div className="checkout__row">
                <div className="checkout__field">
                  <label htmlFor="prenom">Prenom *</label>
                  <input id="prenom" name="prenom" value={adresse.prenom} onChange={handleAdresseChange} placeholder="Votre prenom" required />
                </div>
                <div className="checkout__field">
                  <label htmlFor="nom">Nom *</label>
                  <input id="nom" name="nom" value={adresse.nom} onChange={handleAdresseChange} placeholder="Votre nom" required />
                </div>
              </div>
              <div className="checkout__field">
                <label htmlFor="rue">Adresse *</label>
                <input id="rue" name="rue" value={adresse.rue} onChange={handleAdresseChange} placeholder="Numero et nom de rue" required />
              </div>
              <div className="checkout__field">
                <label htmlFor="complement">Complement</label>
                <input id="complement" name="complement" value={adresse.complement} onChange={handleAdresseChange} placeholder="Batiment, etage, code..." />
              </div>
              <div className="checkout__row">
                <div className="checkout__field">
                  <label htmlFor="code_postal">Code postal *</label>
                  <input id="code_postal" name="code_postal" value={adresse.code_postal} onChange={handleAdresseChange} placeholder="75000" maxLength={5} required />
                </div>
                <div className="checkout__field">
                  <label htmlFor="ville">Ville *</label>
                  <input id="ville" name="ville" value={adresse.ville} onChange={handleAdresseChange} placeholder="Paris" required />
                </div>
              </div>
              <div className="checkout__field">
                <label htmlFor="telephone">Telephone *</label>
                <input id="telephone" name="telephone" type="tel" value={adresse.telephone} onChange={handleAdresseChange} placeholder="06 12 34 56 78" required />
              </div>
              <div className="checkout__actions">
                <Link to="/panier" className="btn-outline">Retour au panier</Link>
                <button type="submit" className="btn-or">Continuer</button>
              </div>
            </form>
          </div>
        )}

        {/* === ETAPE 2: RECAPITULATIF === */}
        {step === 2 && (
          <div className="checkout__section">
            <h2 className="checkout__section-titre">Recapitulatif de votre commande</h2>

            <div className="checkout__recap-grid">
              {/* Articles */}
              <div className="checkout__recap-articles">
                <h3>{count} article{count > 1 ? 's' : ''}</h3>
                {items.map(item => (
                  <div key={item.id} className="checkout__recap-item">
                    <div className="checkout__recap-img" />
                    <div className="checkout__recap-info">
                      <p className="checkout__recap-nom">{item.nom}</p>
                      <p className="checkout__recap-qty">Quantite : {item.quantite}</p>
                    </div>
                    <p className="checkout__recap-prix">{(item.prix * item.quantite).toFixed(2)} EUR</p>
                  </div>
                ))}
              </div>

              {/* Adresse + totaux */}
              <div className="checkout__recap-sidebar">
                <div className="checkout__recap-box">
                  <h3>Livraison</h3>
                  <p>{adresse.prenom} {adresse.nom}</p>
                  <p>{adresse.rue}</p>
                  {adresse.complement && <p>{adresse.complement}</p>}
                  <p>{adresse.code_postal} {adresse.ville}</p>
                  <p>{adresse.telephone}</p>
                  <button className="checkout__edit-btn" onClick={() => setStep(1)}>Modifier</button>
                </div>

                <div className="checkout__recap-box">
                  <h3>Total</h3>
                  <div className="checkout__recap-line">
                    <span>Sous-total</span><span>{total.toFixed(2)} EUR</span>
                  </div>
                  <div className="checkout__recap-line">
                    <span>Livraison</span>
                    <span style={{ color: fraisLivraison === 0 ? '#4caf6e' : 'inherit' }}>
                      {fraisLivraison === 0 ? 'GRATUITE' : `${fraisLivraison.toFixed(2)} EUR`}
                    </span>
                  </div>
                  {fraisLivraison > 0 && (
                    <p className="checkout__recap-hint">Livraison offerte des 50 EUR d'achat</p>
                  )}
                  <div className="checkout__recap-total">
                    <span>Total TTC</span><span>{totalFinal.toFixed(2)} EUR</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="checkout__actions">
              <button className="btn-outline" onClick={() => setStep(1)}>Retour</button>
              <button className="btn-or" onClick={() => setStep(3)}>Proceder au paiement</button>
            </div>
          </div>
        )}

        {/* === ETAPE 3: PAIEMENT === */}
        {step === 3 && (
          <div className="checkout__section">
            <h2 className="checkout__section-titre">Paiement securise</h2>

            <div className="checkout__payment-grid">
              <div className="checkout__payment-form">
                <div className="checkout__payment-badges">
                  <span>🔒 Paiement 100% securise</span>
                  <span>⚡ Stripe</span>
                </div>

                {/* Formulaire Stripe (vrai paiement) */}
                {stripePromise ? (
                  <Elements stripe={stripePromise}>
                    <StripePaymentForm
                      adresse={adresse}
                      onSuccess={handlePaymentSuccess}
                      invite={!isLoggedIn}
                      email={emailInvite}
                      items={items}
                    />
                  </Elements>
                ) : (
                  <div className="checkout__error" role="alert">
                    Paiement non configure : ajoute REACT_APP_STRIPE_PUBLIC_KEY dans Frontend/.env
                    puis relance npm start.
                  </div>
                )}

                <p className="checkout__payment-note">
                  Vos donnees bancaires sont traitees directement par Stripe (certifie PCI-DSS).
                  Elles ne sont jamais stockees sur nos serveurs.
                </p>
              </div>

              <div className="checkout__payment-sidebar">
                <div className="checkout__recap-box">
                  <h3>Votre commande</h3>
                  {items.map(item => (
                    <div key={item.id} className="checkout__recap-line">
                      <span>{item.nom} x{item.quantite}</span>
                      <span>{(item.prix * item.quantite).toFixed(2)} EUR</span>
                    </div>
                  ))}
                  <div className="checkout__recap-total">
                    <span>Total</span><span>{totalFinal.toFixed(2)} EUR</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="checkout__actions" style={{ marginTop: 16 }}>
              <button className="btn-outline" onClick={() => setStep(2)}>Retour</button>
            </div>
          </div>
        )}

        {/* === ETAPE 4: CONFIRMATION === */}
        {step === 4 && (
          <div className="checkout__section checkout__confirmation">
            <div className="checkout__confirmation-icon">✓</div>
            <h2 className="checkout__confirmation-titre">Commande confirmee !</h2>
            <p className="checkout__confirmation-id">Commande #{orderId}</p>

            {reference ? (
              <>
                <div className="checkout__reference">
                  <span className="checkout__reference-label">Votre référence de suivi</span>
                  <strong className="checkout__reference-code">{reference}</strong>
                </div>
                <p className="checkout__confirmation-text">
                  Conservez cette référence : accompagnée de votre adresse
                  <strong> {emailInvite}</strong>, elle vous permet de suivre votre
                  commande à tout moment, sans compte.
                </p>
              </>
            ) : (
              <p className="checkout__confirmation-text">
                Merci pour votre achat ! Vous pouvez suivre l'état de votre commande
                depuis votre espace client.
              </p>
            )}

            <div className="checkout__confirmation-details">
              <div><span>Livraison estimee :</span> <strong>2-5 jours ouvrables</strong></div>
              <div><span>Adresse :</span> <strong>{adresse.rue}, {adresse.code_postal} {adresse.ville}</strong></div>
            </div>
            <div className="checkout__actions" style={{ justifyContent: 'center' }}>
              {reference
                ? <Link to="/suivi" className="btn-or">Suivre ma commande</Link>
                : <Link to="/mon-compte" className="btn-or">Voir mes commandes</Link>}
              <Link to="/catalogue" className="btn-outline">Continuer les achats</Link>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

/* ── Formulaire de paiement Stripe ─────────────────────────
   Récupère un PaymentIntent côté serveur (montant recalculé
   en BDD), confirme le paiement par carte, puis crée la
   commande avec l'identifiant du paiement vérifié. */
function StripePaymentForm({ adresse, onSuccess, invite, email, items }) {
  const stripe   = useStripe();
  const elements = useElements();

  const [clientSecret, setClientSecret] = useState(null);
  const [montant, setMontant]           = useState(null);  // total officiel serveur
  const [payError, setPayError]         = useState('');
  const [paying, setPaying]             = useState(false);

  useEffect(() => {
    // Deux routes distinctes selon le parcours. Dans les deux cas, le montant
    // affiché ici provient du serveur : le panier local ne fait foi pour rien.
    const intention = invite
      ? paymentService.createIntentInvite(
          email,
          items.map(i => ({ product_id: i.product_id ?? i.id, quantite: i.quantite })),
        )
      : paymentService.createIntent();

    intention
      .then(d => { setClientSecret(d.client_secret); setMontant(d.total); })
      .catch(e => setPayError(e.message));
  }, [invite, email, items]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret || paying) return;
    setPaying(true);
    setPayError('');
    try {
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: `${adresse.prenom} ${adresse.nom}`.trim(),
            ...(invite ? { email } : {}),
          },
        },
      });

      if (result.error) {
        setPayError(result.error.message);
        return;
      }
      if (result.paymentIntent?.status === 'succeeded') {
        // Paiement OK → création de la commande (le serveur re-vérifie tout)
        const data = invite
          ? await orderService.createInvite({
              email,
              adresse_livraison: adresse,
              items: items.map(i => ({ product_id: i.product_id ?? i.id, quantite: i.quantite })),
              stripe_payment_id: result.paymentIntent.id,
            })
          : await orderService.create({
              adresse_livraison: adresse,
              stripe_payment_id: result.paymentIntent.id,
            });
        onSuccess(data.order?.id ?? '—', data.order?.reference ?? null);
      }
    } catch (err) {
      setPayError(err.message);
    } finally {
      setPaying(false);
    }
  }

  if (!clientSecret && !payError) return <div className="spinner" />;

  return (
    <form onSubmit={handleSubmit}>
      {clientSecret && (
        <div className="checkout__field">
          <label>Carte bancaire</label>
          <div style={{
            padding: '14px 16px', background: '#1a1a1a',
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4,
          }}>
            <CardElement options={CARD_STYLE} />
          </div>
          <p style={{ fontSize: '0.78rem', color: 'var(--gris)', marginTop: 8 }}>
            Mode test : utilisez 4242 4242 4242 4242, une date future et un CVC quelconque.
          </p>
        </div>
      )}

      {payError && <div className="checkout__error" role="alert">{payError}</div>}

      {clientSecret && (
        <>
          <div className="checkout__payment-total">
            <span>Total a payer :</span>
            <span className="checkout__payment-amount">
              {montant != null ? montant.toFixed(2) : '—'} EUR
            </span>
          </div>
          <button type="submit"
            className={`btn-or checkout__pay-btn${paying ? ' checkout__pay-btn--loading' : ''}`}
            disabled={!stripe || paying}>
            {paying ? 'Traitement en cours...' : `Payer ${montant != null ? montant.toFixed(2) : ''} EUR`}
          </button>
        </>
      )}
    </form>
  );
}